import swal from "sweetalert";
import axios from 'axios';
import { getApiUrl } from '../utils/ApiUtils';
import {
  loginConfirmedAction,
  logout,
} from '../store/actions/AuthActions';

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  confirmPasswordReset,
  verifyPasswordResetCode,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  unlink,
  reload,
  getIdTokenResult,
  updatePassword,
  reauthenticateWithCredential,
  AuthErrorCodes
} from "firebase/auth";
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Enhanced error handling with security considerations
const RATE_LIMIT_ERRORS = new Set([
  'auth/too-many-requests',
  'auth/quota-exceeded'
]);

const SECURITY_SENSITIVE_ERRORS = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/requires-recent-login'
]);


export async function signUp(email, password) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    if (error.code === 'auth/email-already-in-use') {
      // Resume a partially completed registration only by proving the password.
      // An email lookup alone must never establish ownership of the account.
      try { return await signInWithEmailAndPassword(auth, email, password); }
      catch (signInError) {
        if (['auth/network-request-failed', 'auth/too-many-requests'].includes(signInError.code)) throw signInError;
        throw Object.assign(new Error('Kontot finns redan. Logga in med ditt lösenord eller välj Fortsätt med Google.'),
          { code: 'auth/email-already-in-use' });
      }
    }
    throw error;
  }
}

// Enhanced login with better error handling
export async function login(email, password) {
  try { return await signInWithEmailAndPassword(auth, email, password); }
  catch (error) {
    if (['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential'].includes(error.code)) {
      throw { code: 'auth/invalid-credential', message: 'E-post eller lösenord är felaktigt' };
    }
    throw error;
  }
}

// Enhanced function to check available sign-in methods
/**
 * Returns which providers are attached to the CURRENTLY logged-in user, read
 * directly from `auth.currentUser.providerData`. Unlike
 * `checkUserSignInMethods`, this is not affected by Firebase's email
 * enumeration protection — it reads the real account, not a probe.
 *
 * Use this whenever you already have an authenticated user (settings page,
 * account management). Pre-login flows must never depend on email probes.
 */
export async function getCurrentUserProviders() {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) {
    return {
      hasPassword: false,
      hasGoogle: false,
      hasFacebook: false,
      methods: [],
      isEmpty: true,
    };
  }
  await reload(user);
  const ids = (user.providerData || []).map((p) => p.providerId);
  return {
    hasPassword: ids.includes('password'),
    hasGoogle: ids.includes('google.com'),
    hasFacebook: ids.includes('facebook.com'),
    methods: ids,
    isEmpty: ids.length === 0,
  };
}

export async function checkUserSignInMethods(email) {
  try {
    // Input validation
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      throw new Error('Invalid email address');
    }

    const methods = await fetchSignInMethodsForEmail(auth, email);

    return {
      hasPassword: methods.includes('password'),
      hasGoogle: methods.includes('google.com'),
      hasFacebook: methods.includes('facebook.com'),
      methods: methods,
      isEmpty: methods.length === 0
    };
  } catch (error) {

    // Don't expose internal errors
    if (error.code === 'auth/invalid-email') {
      throw error;
    }

    // For other errors, return safe defaults
    return {
      hasPassword: false,
      hasGoogle: false,
      hasFacebook: false,
      methods: [],
      isEmpty: true,
      error: true
    };
  }
}

// The backend accepts requests durably and delivers Firebase action links
// through LedgerLoop's email sender. Never probe account providers here.
export async function sendPasswordReset(email) {
  if (typeof email !== 'string' || !email.includes('@')) throw { code: 'auth/invalid-email' };
  try {
    const response = await axios.post(getApiUrl('auth/password-reset'),
      { email: email.trim().toLowerCase() }, { timeout: 15000 });
    if (response.status !== 202 || response.data?.status !== 'accepted') throw new Error('Unexpected reset response');
  } catch (error) {
    const status = error.response?.status;
    throw {
      code: status === 429 ? 'auth/too-many-requests' : status === 400 ? 'auth/invalid-email' : 'auth/network-request-failed',
      message: status === 429 ? 'För många försök. Vänta en stund och försök igen.'
        : status === 400 ? 'Ange en giltig e-postadress.' : 'Kunde inte begära återställning. Försök igen om en stund.',
    };
  }
  return { success: true };
}

async function requireRecentFirebaseLogin(user) {
  const result = await getIdTokenResult(user, true);
  // Token refresh changes iat, not the verified authentication time.
  const authTime = Number(result.claims.auth_time);
  const age = Math.floor(Date.now() / 1000) - authTime;
  if (!Number.isFinite(authTime) || age < 0 || age > 5 * 60) {
    throw { code: 'auth/requires-recent-login', message: 'Logga in igen innan du ändrar inloggningsmetoder.' };
  }
}

async function recordCredentialChange(user) {
  const idToken = await user.getIdToken(true);
  await axios.post(getApiUrl('auth/security-change'), { idToken }, { withCredentials: true, timeout: 15000 });
}

// Function to allow users to add password to Google-only accounts
export async function addPasswordToCurrentUser(newPassword) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw { code: 'auth/no-current-user', message: 'Ingen användare är inloggad' };
    }

    // Validate password strength
    if (!newPassword || newPassword.length < 6) {
      throw { code: 'auth/weak-password', message: 'Lösenordet måste vara minst 6 tecken' };
    }

    await requireRecentFirebaseLogin(user);

    // Google's enumeration-protection contract requires accounts:signUp with
    // an existing ID token for password linking. No account lookup or merge.
    const idToken = await user.getIdToken(true);
    let linked;
    try {
      linked = await axios.post(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${encodeURIComponent(firebaseConfig.apiKey)}`,
        { idToken, email: user.email, password: newPassword, returnSecureToken: true }, { timeout: 15000 });
    } catch (error) {
      const reason = error.response?.data?.error?.message?.split(' : ')[0];
      const codes = { EMAIL_EXISTS: 'auth/provider-already-linked', CREDENTIAL_TOO_OLD_LOGIN_AGAIN: 'auth/requires-recent-login',
        WEAK_PASSWORD: 'auth/weak-password', TOO_MANY_ATTEMPTS_TRY_LATER: 'auth/too-many-requests' };
      throw { code: codes[reason] || 'auth/password-link-failed', message: 'Kunde inte lägga till lösenordet. Logga in igen och försök på nytt.' };
    }
    if (linked.data.localId !== user.uid) throw { code: 'auth/identity-mismatch', message: 'Kunde inte bekräfta inloggningsmetoden.' };
    await reload(user);
    await recordCredentialChange(user);

    return { success: true, message: 'Lösenord har lagts till på ditt konto' };

  } catch (error) {

    if (error.code === 'auth/provider-already-linked') {
      throw {
        code: error.code,
        message: 'Detta konto har redan ett lösenord'
      };
    }

    if (error.code === 'auth/credential-already-in-use') {
      throw {
        code: error.code,
        message: 'Denna e-postadress används redan med ett lösenord'
      };
    }

    throw error;
  }
}

// Enhanced function to unlink providers (for account management)
export async function unlinkProvider(providerId) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw { code: 'auth/no-current-user', message: 'Ingen användare är inloggad' };
    }

    // Security check - don't allow unlinking if it's the only sign-in method
    await reload(user);
    const usable = (user.providerData || []).filter(p => ['password', 'google.com'].includes(p.providerId));
    if (!usable.some(p => p.providerId !== providerId)) {
      throw {
        code: 'auth/cannot-unlink-last-provider',
        message: 'Du kan inte ta bort den enda inloggningsmetoden'
      };
    }

    await requireRecentFirebaseLogin(user);
    await unlink(user, providerId);
    await recordCredentialChange(user);
    return { success: true };

  } catch (error) {
    throw error;
  }
}

// Verify reset code with enhanced security
export async function verifyResetCode(code) {
  try {
    // Input validation
    if (!code || typeof code !== 'string' || code.length < 10) {
      throw { code: 'auth/invalid-action-code', message: 'Ogiltig återställningskod' };
    }

    const email = await verifyPasswordResetCode(auth, code);
    return { success: true, email };
  } catch (error) {
    throw error;
  }
}

// Confirm password reset with enhanced validation
export async function confirmPasswordResetWithCode(code, newPassword) {
  try {
    // Input validation
    if (!code || typeof code !== 'string') {
      throw { code: 'auth/invalid-action-code', message: 'Ogiltig återställningskod' };
    }

    if (!newPassword || newPassword.length < 8) {
      throw { code: 'auth/weak-password', message: 'Lösenordet måste vara minst 8 tecken' };
    }

    // Additional password strength validation
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      throw {
        code: 'auth/weak-password',
        message: 'Lösenordet måste innehålla stora och små bokstäver samt siffror'
      };
    }

    await confirmPasswordReset(auth, code, newPassword);
    return { success: true };
  } catch (error) {
    throw error;
  }
}

// Enhanced error formatting with security considerations
export function formatError(firebaseError) {
  // Don't log sensitive information
  const errorCode = firebaseError?.code || 'unknown';

  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      swal("Fel vid inloggning", "E-post eller lösenord är felaktigt. Om du registrerade dig med Google, prova 'Fortsätt med Google'.", "error");
      break;

    case 'auth/google-only-user':
      swal({
        title: "Använd Google för inloggning",
        text: "Detta konto använder Google för inloggning. Du kan också lägga till ett lösenord i kontoinställningar.",
        icon: "info",
        buttons: {
          cancel: "OK",
          confirm: {
            text: "Fortsätt med Google",
            value: "google"
          }
        }
      }).then((value) => {
        if (value === "google") {
          // Trigger Google sign-in
          window.dispatchEvent(new CustomEvent('triggerGoogleSignIn'));
        }
      });
      break;

    case 'auth/account-exists-different-credential':
      swal("Konto finns redan", "Ett konto med denna e-postadress finns redan med en annan inloggningsmetod. Försök logga in istället.", "warning");
      break;

    case 'auth/too-many-requests':
      swal("För många försök", "För många inloggningsförsök från denna enhet. Vänta en stund och försök igen.", "error");
      break;


    case 'auth/invalid-email':
      swal("Ogiltig e-post", "E-postadressen är inte giltig.", "error");
      break;

    case 'auth/weak-password':
      swal("Svagt lösenord", firebaseError.message || "Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror.", "error");
      break;

    case 'auth/email-already-in-use':
      swal("E-post används redan", "E-postadressen används redan. Försök logga in istället.", "error");
      break;

    case 'auth/invalid-action-code':
      swal("Ogiltig länk", "Återställningslänken är ogiltig eller felaktig.", "error");
      break;

    case 'auth/expired-action-code':
      swal("Utgången länk", "Återställningslänken har gått ut. Begär en ny.", "error");
      break;

    case 'auth/user-disabled':
      swal("Konto inaktiverat", "Detta konto har inaktiverats. Kontakta support.", "error");
      break;

    case 'auth/operation-not-allowed':
      swal("Operation inte tillåten", "Denna inloggningsmetod är inte aktiverad.", "error");
      break;

    case 'auth/requires-recent-login':
      swal("Säkerhetsverifiering krävs", "Du måste logga in igen för att utföra denna åtgärd.", "warning");
      break;

    default:
      // Generic error for unknown cases - don't expose internal details
      swal("Ett fel inträffade", "Ett oväntat fel inträffade. Försök igen senare.", "error");
      return '';
  }
}

// Existing functions remain the same...
export function saveTokenInLocalStorage(tokenDetails) {

  tokenDetails.expireDate = new Date(
    new Date().getTime() + tokenDetails._tokenResponse.expiresIn * 1000,
  );
  localStorage.setItem('userDetails', JSON.stringify(tokenDetails));
}

export function runLogoutTimer(dispatch, timer, history) {
  setTimeout(() => {
    dispatch(logout(history));
  }, timer);
}

export function checkAutoLogin(dispatch, history) {
  const tokenDetailsString = localStorage.getItem('userDetails');
  let tokenDetails = '';
  if (!tokenDetailsString) {
    dispatch(logout(history));
    return;
  }

  try {
    tokenDetails = JSON.parse(tokenDetailsString);
  } catch (error) {
    dispatch(logout(history));
    return;
  }

  let expireDate = new Date(tokenDetails.expireDate);
  let todaysDate = new Date();

  if (todaysDate > expireDate) {
    dispatch(logout(history));
    return;
  }

  const selectedCompanyString = localStorage.getItem('selectedCompany');
  let selectedCompany = {
    companyName: '',
    companyId: '',
    companyRegNo: '',
  };
  if (selectedCompanyString) {
    try {
      selectedCompany = JSON.parse(selectedCompanyString);
    } catch (error) {
      // Continue with default selectedCompany
    }
  }

  dispatch(loginConfirmedAction(tokenDetails, selectedCompany));

  const timer = expireDate.getTime() - todaysDate.getTime();
  runLogoutTimer(dispatch, timer, history);
}
